import { createDeserializer, handleJsonApiError } from "@jsonapi-serde/client";
import { queryOptions } from "@tanstack/react-query";
import { z } from "zod/mini";
import { zj } from "zod-joda/mini";
import { apiUrl } from "@/utils/api.ts";
import { localZoneId } from "@/utils/datetime.ts";

const bidTargetSchema = z.enum(["parcel", "parcel_combination", "global"]);
export type BidTarget = z.output<typeof bidTargetSchema>;

const bidSchema = z.object({
    registrationId: z.string(),
    target: bidTargetSchema,
    index: z.int().check(z.nonnegative()),
    amount: z.int().check(z.nonnegative()),
    previousAmount: z.nullable(z.int().check(z.nonnegative())),
    placedOn: z.nullable(zj.zonedDateTime()),
    previousPlacedOn: z.nullable(zj.zonedDateTime()),
});

export type Bid = z.output<typeof bidSchema>;

const biddingIncrementSchema = z.object({
    target: bidTargetSchema,
    index: z.int().check(z.nonnegative()),
    amount: z.int().check(z.positive()),
});

export type BiddingIncrement = z.output<typeof biddingIncrementSchema>;

const winningBidSchema = z.object({
    registrationId: z.string(),
    target: bidTargetSchema,
    index: z.int().check(z.nonnegative()),
    amount: z.int().check(z.nonnegative()),
});

export const auctionSessionAttributesSchema = z.object({
    startedAt: z.pipe(
        zj.zonedDateTime(),
        z.transform((value) => value.withZoneSameInstant(localZoneId)),
    ),
    concludedAt: z.nullable(
        z.pipe(
            zj.zonedDateTime(),
            z.transform((value) => value.withZoneSameInstant(localZoneId)),
        ),
    ),
    bids: z.array(bidSchema),
    lastBid: z.nullable(bidSchema),
    biddingIncrements: z.array(biddingIncrementSchema),
    winningBids: z.array(winningBidSchema),
    leaderOverrideId: z.nullable(z.string()),
    leaderOverrideAllowed: z.boolean(),
});

export const deserializeAuctionSession = createDeserializer({
    type: "auction_session",
    cardinality: "one",
    attributesSchema: auctionSessionAttributesSchema,
});

export type AuctionSession = ReturnType<typeof deserializeAuctionSession>["data"];

export const createAuctionSessionQueryOptionsFactory = (authFetch: typeof fetch) => ({
    get: (auctionId: string) =>
        queryOptions({
            queryKey: ["auction", auctionId, "session"],
            queryFn: async ({ signal }) => {
                const response = await authFetch(apiUrl(`/auctions/${auctionId}/session`), {
                    signal,
                });
                await handleJsonApiError(response);
                return deserializeAuctionSession(await response.json()).data;
            },
        }),
    getPreview: (auctionId: string) =>
        queryOptions({
            queryKey: ["auction", auctionId, "session", "preview"],
            queryFn: async ({ signal }) => {
                const response = await authFetch(apiUrl(`/auctions/${auctionId}/session/preview`), {
                    signal,
                });
                await handleJsonApiError(response);
                return deserializeAuctionSession(await response.json()).data;
            },
        }),
});
